// Inline territory-atlas map for the expanded contact card. Renders
// the same UK-wide atlas + contact pin the modal shows, but as a
// permanent panel (no chrome, no click-out close) so an admin never
// has to click "MAP" — the contact's geography is visible the moment
// they expand the row.
//
// Deliberately shares data sources with ContactPostcodeMapModal so
// there's no duplicate map or divergent styling:
//   * ``GET /api/territory/all-franchisees``  → cached FeatureCollection
//     of every franchisee's dissolved territory (fills + outlines).
//   * ``https://api.postcodes.io/postcodes/{postcode}`` → free public
//     geocoder for a single postcode → { latitude, longitude }.

import { useEffect, useMemo, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { Loader2, AlertTriangle } from "lucide-react";
import api from "@/lib/api";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

// Legend chip — kept static because the atlas contains ~80 territories
// so per-territory legend rows would be noise. This just tells the
// admin "the colour spread you're seeing IS the franchise territories".
function LegendSwatch() {
  return (
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-stone-600">
      <span>Existing franchise territories</span>
      <span className="inline-flex gap-1">
        <span className="w-3 h-3 rounded-full" style={{ background: "#fed7aa" }} />
        <span className="w-3 h-3 rounded-full" style={{ background: "#fca5a5" }} />
        <span className="w-3 h-3 rounded-full" style={{ background: "#f97316" }} />
      </span>
    </div>
  );
}

export default function ContactTerritoryMapInline({ contact, height = 320, fill = false, className = "" }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const [atlas, setAtlas] = useState(null);
  const [geocode, setGeocode] = useState(null);
  const [error, setError] = useState(null);
  const [geocodeError, setGeocodeError] = useState(null);
  const [loading, setLoading] = useState(true);

  const postcode = useMemo(
    () => (contact?.postcode || "").trim().toUpperCase(),
    [contact],
  );

  // Fetch atlas + geocode in parallel — only when this contact row is
  // actually expanded (parent controls mount/unmount).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null); setGeocodeError(null);
      const [atlasP, geoP] = await Promise.allSettled([
        api.get("/territory/all-franchisees"),
        postcode
          ? fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(postcode)}`).then((r) => r.json())
          : Promise.resolve(null),
      ]);
      if (cancelled) return;
      if (atlasP.status === "fulfilled") setAtlas(atlasP.value.data);
      else setError(atlasP.reason?.message || "Could not load territory atlas");
      if (geoP.status === "fulfilled" && geoP.value?.result) {
        setGeocode({
          lat: geoP.value.result.latitude,
          lng: geoP.value.result.longitude,
          admin_district: geoP.value.result.admin_district,
        });
      } else if (postcode) {
        setGeocodeError(
          geoP.status === "fulfilled"
            ? (geoP.value?.error || "Postcode not recognised")
            : "Postcode lookup failed",
        );
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [postcode]);

  // Boot Mapbox once we have both container + atlas AND the container
  // has real dimensions. Booting into a 0×0 flex child leaves the GL
  // canvas with a stale viewport that never re-invalidates the fills
  // layer paint state — even after ``resize()`` fires — so we wait
  // for a non-zero width/height before instantiating the map.
  useEffect(() => {
    if (!MAPBOX_TOKEN || !containerRef.current || !atlas || mapRef.current) return;

    const tryBoot = () => {
      const el = containerRef.current;
      if (!el || mapRef.current) return true;
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (!w || !h) return false;

      mapboxgl.accessToken = MAPBOX_TOKEN;
      const map = new mapboxgl.Map({
        container: el,
        style: "mapbox://styles/mapbox/light-v11",
        center: [-2.5, 54.0],
        zoom: 5.2,
        attributionControl: false,
      });
      mapRef.current = map;

      map.on("load", () => {
        // Force a resize immediately in case the container grew
        // between construction and load (common inside a flex row
        // that only settles after siblings paint).
        try { map.resize(); } catch (_e) { /* noop */ }

        const fills = atlas.geojson || atlas.fills || null;
        const outlines = atlas.outlines || null;
        if (fills) {
          map.addSource("cti-fills", { type: "geojson", data: fills });
          map.addLayer({
            id: "cti-fills-layer", type: "fill", source: "cti-fills",
            paint: {
              "fill-color": ["coalesce", ["get", "color"], "#a3e635"],
              "fill-opacity": 0.45,
            },
          });
        }
        if (outlines) {
          map.addSource("cti-outlines", { type: "geojson", data: outlines });
          map.addLayer({
            id: "cti-outlines-layer", type: "line", source: "cti-outlines",
            paint: {
              "line-color": ["coalesce", ["get", "color"], "#65a30d"],
              "line-width": 1.2, "line-opacity": 0.95,
            },
          });
          // Territory-name label overlay — mirrors the Territory
          // Builder ``franchisee-name-label`` symbol layer so each
          // franchise polygon prints its organisation + owner name
          // at the centroid.  Displayed as standard on the inline
          // map (no toggle) per Sam's spec.
          map.addLayer({
            id: "cti-outlines-label",
            type: "symbol",
            source: "cti-outlines",
            minzoom: 5,
            layout: {
              "text-field": [
                "case",
                ["all",
                  ["has", "name"], ["!=", ["get", "name"], ""],
                  ["has", "owner_name"], ["!=", ["get", "owner_name"], ""],
                ],
                ["concat", ["get", "name"], "\n· ", ["get", "owner_name"]],
                ["all", ["has", "name"], ["!=", ["get", "name"], ""]],
                ["get", "name"],
                ["has", "franchise_number"], ["concat", "#", ["get", "franchise_number"]],
                "",
              ],
              "text-size": 11,
              "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
              "text-anchor": "center",
              "symbol-placement": "point",
              "text-allow-overlap": false,
              "text-ignore-placement": false,
              "text-line-height": 1.15,
            },
            paint: {
              "text-color": "#0c0a09",
              "text-halo-color": "#ffffff",
              "text-halo-width": 2,
              "text-halo-blur": 0.4,
            },
          });
        }
        if (geocode?.lat && geocode?.lng) {
          new mapboxgl.Marker({ color: "#dc2626" })
            .setLngLat([geocode.lng, geocode.lat])
            .setPopup(
              new mapboxgl.Popup({ offset: 20, closeButton: false }).setHTML(
                `<div style="font-family: system-ui; font-size: 12px; padding: 4px 6px;">
                  <div style="font-weight: 700;">${contact?.first_name || ""} ${contact?.last_name || ""}</div>
                  <div style="color: #57534e;">${postcode}</div>
                  ${geocode.admin_district ? `<div style="color: #78716c;">${geocode.admin_district}</div>` : ""}
                </div>`,
              ),
            )
            .addTo(map)
            .togglePopup();
          // Zoom 7 gives enough geographical context to see nearby
          // franchisee territories — 8.5 (used by the 800px modal)
          // was too tight for the smaller inline viewport and hid
          // surrounding territories.
          map.flyTo({ center: [geocode.lng, geocode.lat], zoom: 7, duration: 900 });
        }
        // Belt-and-braces resize once layers are painted.
        try { map.resize(); } catch (_e) { /* noop */ }
      });
      return true;
    };

    if (tryBoot()) return undefined;
    // Container not yet laid out — observe until it has size, then
    // boot exactly once.
    const ro = new ResizeObserver(() => { if (tryBoot()) ro.disconnect(); });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [atlas, geocode, contact, postcode]);

  // Fly to a new geocode when it arrives after the map has already
  // loaded (rare — the atlas usually resolves after geocode).
  useEffect(() => {
    if (!mapRef.current || !geocode?.lat) return;
    if (!mapRef.current.loaded()) return;
    mapRef.current.flyTo({
      center: [geocode.lng, geocode.lat], zoom: 7, duration: 900,
    });
  }, [geocode]);

  // Teardown on unmount (row collapse).
  useEffect(() => {
    return () => {
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    };
  }, []);

  // Mapbox needs an explicit ``.resize()`` call when its container
  // changes size — otherwise the canvas keeps its initial dimensions
  // and the map appears letterboxed inside a taller panel. Only used
  // in ``fill`` mode where the panel height is decided by siblings.
  useEffect(() => {
    if (!fill || !containerRef.current) return;
    const ro = new ResizeObserver(() => {
      if (mapRef.current && mapRef.current.loaded()) {
        mapRef.current.resize();
      }
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [fill]);

  return (
    <div
      className={`relative overflow-hidden bg-stone-100 ${fill ? "flex-1 min-h-[280px] h-full w-full" : "rounded-xl border border-stone-200"} ${className}`}
      data-testid={`contact-territory-map-${contact?.id || "?"}`}
    >
      <div className="absolute top-2 right-2 z-10 bg-white/95 backdrop-blur-sm rounded-md border border-stone-200 px-2 py-1 shadow-sm">
        <LegendSwatch />
      </div>
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center z-10 bg-white/60">
          <div className="flex items-center gap-2 text-sm text-stone-600">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading atlas…
          </div>
        </div>
      )}
      {!MAPBOX_TOKEN && (
        <div className="absolute inset-0 flex items-center justify-center p-6 text-center">
          <div className="text-sm text-stone-600">Map unavailable — <code>REACT_APP_MAPBOX_TOKEN</code> not configured.</div>
        </div>
      )}
      {error && (
        <div className="absolute top-3 left-3 right-32 z-10 bg-red-50 border border-red-200 text-red-900 text-xs rounded p-2 flex items-start gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {error}
        </div>
      )}
      {geocodeError && !loading && (
        <div className="absolute bottom-3 left-3 right-3 z-10 bg-amber-50 border border-amber-300 text-amber-900 text-xs rounded p-2 flex items-start gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          Postcode <span className="font-mono">{postcode || "?"}</span> couldn&apos;t be geocoded ({geocodeError}). Showing UK atlas without a pin.
        </div>
      )}
      <div
        ref={containerRef}
        className={fill ? "w-full h-full absolute inset-0" : "w-full"}
        style={fill ? undefined : { height }}
      />
    </div>
  );
}
